import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import {
  getPlatformFeePercent,
  getPlatformFeeRate,
  resolvePlatformFeePercent,
  resolvePlatformFeeRate,
} from '@/utils/platformFee';

/**
 * Read the current org's platform fee. Falls back to 1.5% when neither
 * brackets nor a flat percent are set. Reuses the same React Query key
 * that Dashboard / GenerateQuote already use so React Query de-dupes
 * automatically — one network call per org across the whole app.
 *
 * The fetch always requests `include=platform_fee_brackets` so bracket
 * data lands on the shared cache entry; consumers that only need the
 * flat percent ignore the extra field.
 *
 * @returns {{
 *   percent: number,                              // flat (or first-bracket) — legacy
 *   rate: number,                                 // flat as decimal      — legacy
 *   isReady: boolean,
 *   organization: object|undefined,               // raw record if callers need brackets
 *   resolveForSubtotal: (subtotal:number) => { percent:number, rate:number },
 * }}
 */
export const usePlatformFeeRate = () => {
  const { userProfile } = useAuthStore();
  const orgId = userProfile?.org_id;
  const { data: organization } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => apiClient.getOrganizationById(orgId, { include: 'platform_fee_brackets' }),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  // Bracket-aware resolver — pass an option's subtotal (operator_cost +
  // broker_markup) and get back the effective percent + rate for that
  // specific dollar band. Falls through to flat / default when no
  // bracket applies (or the org hasn't configured any).
  const resolveForSubtotal = useCallback(
    (subtotal) => ({
      percent: resolvePlatformFeePercent({ organization, subtotal }),
      rate: resolvePlatformFeeRate({ organization, subtotal }),
    }),
    [organization],
  );

  return {
    // Legacy flat readers — unchanged for callers that don't care about
    // brackets (e.g. labels shown before a subtotal exists).
    percent: getPlatformFeePercent(organization),
    rate: getPlatformFeeRate(organization),
    isReady: !!organization,
    organization,
    resolveForSubtotal,
  };
};
